import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ImageNotSupportedRoundedIcon from '@mui/icons-material/ImageNotSupportedRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import DashboardPageHeader from 'pages/dashboard/DashboardPageHeader';
import VendorDashboardLayout from 'pages/dashboard/VendorDashboardLayout';

const movementLabels = {
  stock_set: 'Ръчна корекция',
  order_material_allocated: 'Отписано за поръчка',
  order_material_adjusted: 'Корекция на поръчка',
  order_material_released: 'Върнато от поръчка'
};

const stockFilterLabels = {
  all: 'Всички наличности',
  under20: 'Под 20 м',
  under100: 'Под 100 м',
  over50: 'Над 50 м',
  customUnder: 'Под избрана стойност',
  customOver: 'Над избрана стойност'
};

const meterSortLabels = {
  codeAsc: 'Код (А–Я)',
  metersAsc: 'Метри: ниски → високи',
  metersDesc: 'Метри: високи → ниски'
};

const formatMeters = (value) =>
  new Intl.NumberFormat('bg-BG', { maximumFractionDigits: 3 }).format(Number(value || 0));

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat('bg-BG', { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(value)
      )
    : '—';

function MaterialImage({ material, size = 180 }) {
  return material.imageUrl ? (
    <Box
      component="img"
      src={material.imageUrl}
      alt={`Цвят ${material.code}`}
      sx={{
        bgcolor: 'grey.100',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        height: size,
        objectFit: 'cover',
        width: size
      }}
    />
  ) : (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: 'grey.100',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        color: 'grey.400',
        display: 'flex',
        height: size,
        justifyContent: 'center',
        width: size
      }}
      title="Няма снимка за този код"
    >
      <ImageNotSupportedRoundedIcon fontSize="small" />
    </Box>
  );
}

export default function Materials() {
  const [materials, setMaterials] = useState([]);
  const [stockDrafts, setStockDrafts] = useState({});
  const [search, setSearch] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [customStockLimit, setCustomStockLimit] = useState('');
  const [appearanceFilter, setAppearanceFilter] = useState('all');
  const [materialFilter, setMaterialFilter] = useState('all');
  const [audienceFilter, setAudienceFilter] = useState('all');
  const [meterSort, setMeterSort] = useState('codeAsc');
  const [isLoading, setIsLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  const [historyMaterial, setHistoryMaterial] = useState(null);
  const [movements, setMovements] = useState([]);
  const [historyError, setHistoryError] = useState('');
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const loadMaterials = async () => {
    setIsLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/materials');
      const nextMaterials = data.materials || [];
      setMaterials(nextMaterials);
      setStockDrafts(
        Object.fromEntries(nextMaterials.map((material) => [material.id, String(material.availableMeters)]))
      );
    } catch (requestError) {
      setError('Материалите не можаха да бъдат заредени. Моля, опитайте отново.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMaterials();
  }, []);

  const materialFilterOptions = useMemo(() => {
    const uniqueAttributes = new Map();
    materials.forEach((material) => {
      (material.materialAttributes || []).forEach((attribute) => {
        uniqueAttributes.set(String(attribute.id), attribute);
      });
    });
    return [...uniqueAttributes.values()].sort((first, second) =>
      first.name.localeCompare(second.name, 'bg')
    );
  }, [materials]);

  const filteredMaterials = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase('bg');
    const customLimit = Number(String(customStockLimit).replace(',', '.'));
    const hasCustomLimit = Number.isFinite(customLimit) && customLimit >= 0;

    const filtered = materials.filter((material) => {
      const matchesSearch =
        !normalizedSearch || material.code.toLocaleLowerCase('bg').includes(normalizedSearch);
      if (!matchesSearch) return false;
      if (appearanceFilter !== 'all' && material.appearanceType !== appearanceFilter) {
        return false;
      }
      if (
        materialFilter !== 'all' &&
        !(material.materialAttributes || []).some(
          (attribute) => String(attribute.id) === materialFilter
        )
      ) {
        return false;
      }
      if (audienceFilter !== 'all' && !(material.audiences || []).includes(audienceFilter)) {
        return false;
      }

      const availableMeters = Number(material.availableMeters);
      if (stockFilter === 'under20') return availableMeters < 20;
      if (stockFilter === 'under100') return availableMeters < 100;
      if (stockFilter === 'over50') return availableMeters > 50;
      if (stockFilter === 'customUnder') return !hasCustomLimit || availableMeters < customLimit;
      if (stockFilter === 'customOver') return !hasCustomLimit || availableMeters > customLimit;
      return true;
    });
    return filtered.sort((first, second) => {
      if (meterSort === 'metersAsc') {
        return Number(first.availableMeters) - Number(second.availableMeters);
      }
      if (meterSort === 'metersDesc') {
        return Number(second.availableMeters) - Number(first.availableMeters);
      }
      return first.code.localeCompare(second.code, 'bg');
    });
  }, [
    materials,
    search,
    stockFilter,
    customStockLimit,
    appearanceFilter,
    materialFilter,
    audienceFilter,
    meterSort
  ]);

  const changeDraft = (id) => (event) => {
    setStockDrafts((current) => ({ ...current, [id]: event.target.value }));
  };

  const saveStock = async (material) => {
    const availableMeters = String(stockDrafts[material.id] ?? '').trim();
    if (!/^\d+(?:[.,]\d{1,3})?$/.test(availableMeters)) {
      setError(`Въведете валиден брой метри за ${material.code} (до 3 знака след десетичната запетая).`);
      return;
    }

    setSavingId(material.id);
    setError('');
    try {
      const { data } = await axios.put(`/materials/${material.id}/stock`, { availableMeters });
      setMaterials((current) =>
        current.map((item) =>
          item.id === material.id
            ? { ...data.material, appearanceType: item.appearanceType }
            : item
        )
      );
      setStockDrafts((current) => ({ ...current, [material.id]: String(data.material.availableMeters) }));
    } catch (requestError) {
      setError(requestError.response?.data?.message || `Количеството за ${material.code} не можа да бъде запазено.`);
    } finally {
      setSavingId(null);
    }
  };

  const openHistory = async (material) => {
    setHistoryMaterial(material);
    setMovements([]);
    setHistoryError('');
    setIsLoadingHistory(true);
    try {
      const { data } = await axios.get(`/materials/${material.id}/movements`);
      setMovements(data.movements || []);
    } catch (requestError) {
      setHistoryError('Историята на материала не можа да бъде заредена.');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  return (
    <VendorDashboardLayout>
      <DashboardPageHeader
        title="Материали"
        subtitle="Наличностите са в метри. При запазване на поръчка посочените материали се отписват автоматично."
        icon={Inventory2RoundedIcon}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          alignItems={{ xs: 'stretch', lg: 'center' }}
          gap={2}
          sx={{ p: { xs: 2, sm: 2.5 } }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">Кодове и наличности</Typography>
            <Typography variant="body2" color="text.secondary">
              Кодове и снимки се синхронизират от каталога с продукти.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexShrink: 0, flexWrap: 'wrap', gap: 1 }}>
            <TextField
              label="Търси код"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: { sm: 200 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            <FormControl sx={{ minWidth: { sm: 190 } }}>
              <InputLabel id="material-stock-filter-label">Наличност</InputLabel>
              <Select
                labelId="material-stock-filter-label"
                label="Наличност"
                value={stockFilter}
                onChange={(event) => setStockFilter(event.target.value)}
              >
                {Object.entries(stockFilterLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: { sm: 155 } }}>
              <InputLabel id="material-appearance-filter-label">Вид</InputLabel>
              <Select
                labelId="material-appearance-filter-label"
                label="Вид"
                value={appearanceFilter}
                onChange={(event) => setAppearanceFilter(event.target.value)}
              >
                <MenuItem value="all">Всички видове</MenuItem>
                <MenuItem value="solid">Едноцветни</MenuItem>
                <MenuItem value="colorful">Цветни</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: { sm: 170 } }}>
              <InputLabel id="material-filter-label">Материал</InputLabel>
              <Select
                labelId="material-filter-label"
                label="Материал"
                value={materialFilter}
                onChange={(event) => setMaterialFilter(event.target.value)}
              >
                <MenuItem value="all">Всички материали</MenuItem>
                {materialFilterOptions.map((attribute) => (
                  <MenuItem key={attribute.id} value={String(attribute.id)}>
                    {attribute.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: { sm: 165 } }}>
              <InputLabel id="material-audience-filter-label">Колекция</InputLabel>
              <Select
                labelId="material-audience-filter-label"
                label="Колекция"
                value={audienceFilter}
                onChange={(event) => setAudienceFilter(event.target.value)}
              >
                <MenuItem value="all">Възрастни и детски</MenuItem>
                <MenuItem value="adult">За възрастни</MenuItem>
                <MenuItem value="kids">Детски</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: { sm: 185 } }}>
              <InputLabel id="material-sort-label">Подреди по</InputLabel>
              <Select
                labelId="material-sort-label"
                label="Подреди по"
                value={meterSort}
                onChange={(event) => setMeterSort(event.target.value)}
              >
                {Object.entries(meterSortLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(stockFilter === 'customUnder' || stockFilter === 'customOver') && (
              <TextField
                label="Метри"
                value={customStockLimit}
                onChange={(event) => setCustomStockLimit(event.target.value)}
                inputProps={{ inputMode: 'decimal' }}
                sx={{ width: { sm: 115 } }}
              />
            )}
          </Box>
        </Stack>
        <Divider />

        {!isLoading && (
          <Typography color="text.secondary" variant="body2" sx={{ px: { xs: 2, sm: 2.5 }, pt: 2 }}>
            {filteredMaterials.length === materials.length
              ? `Общо материали: ${materials.length}`
              : `Показани ${filteredMaterials.length} от ${materials.length} материала`}
          </Typography>
        )}

        {isLoading ? (
          <Stack alignItems="center" sx={{ p: 8 }}>
            <CircularProgress aria-label="Зареждане на материалите" />
          </Stack>
        ) : filteredMaterials.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 4 }}>
            Няма материали с тези филтри.
          </Typography>
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Цвят</TableCell>
                  <TableCell>Код</TableCell>
                  <TableCell align="right">Налични метри</TableCell>
                  <TableCell sx={{ width: 210 }}>Промени наличност</TableCell>
                  <TableCell align="right">История</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMaterials.map((material) => (
                  <TableRow key={material.id} hover>
                    <TableCell>
                      <MaterialImage material={material} size={180} />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={750}>{material.code}</Typography>
                      {!material.imageUrl && (
                        <Typography variant="caption" color="text.secondary">
                          Няма снимка в каталога
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        color={Number(material.availableMeters) <= 0 ? 'warning' : 'default'}
                        label={`${formatMeters(material.availableMeters)} м`}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" gap={1} alignItems="center">
                        <TextField
                          aria-label={`Налични метри за ${material.code}`}
                          value={stockDrafts[material.id] ?? ''}
                          onChange={changeDraft(material.id)}
                          inputProps={{ inputMode: 'decimal' }}
                          sx={{ width: 110 }}
                        />
                        <Button
                          aria-label={`Запази наличност за ${material.code}`}
                          disabled={savingId === material.id}
                          onClick={() => saveStock(material)}
                          size="small"
                          variant="contained"
                        >
                          <SaveRoundedIcon fontSize="small" />
                        </Button>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        onClick={() => openHistory(material)}
                        size="small"
                        startIcon={<HistoryRoundedIcon />}
                      >
                        Виж
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog
        open={Boolean(historyMaterial)}
        onClose={() => setHistoryMaterial(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>История на наличността · {historyMaterial?.code}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {isLoadingHistory ? (
            <Stack alignItems="center" sx={{ p: 6 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : historyError ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {historyError}
            </Alert>
          ) : movements.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 3 }}>
              Все още няма движения за този материал.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Дата</TableCell>
                    <TableCell>Действие</TableCell>
                    <TableCell align="right">Промяна</TableCell>
                    <TableCell align="right">Наличност след това</TableCell>
                    <TableCell>От кого</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movements.map((movement) => (
                    <TableRow key={movement.id}>
                      <TableCell>{formatDate(movement.createdAt)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {movementLabels[movement.movementType] || movement.movementType}
                        </Typography>
                        {movement.orderDetailId && (
                          <Typography variant="caption" color="text.secondary">
                            Поръчка #{movement.orderDetailId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: Number(movement.deltaMeters) < 0 ? 'error.main' : 'success.main' }}
                      >
                        {Number(movement.deltaMeters) > 0 ? '+' : ''}
                        {formatMeters(movement.deltaMeters)} м
                      </TableCell>
                      <TableCell align="right">{formatMeters(movement.balanceAfterMeters)} м</TableCell>
                      <TableCell>{movement.actorName || movement.actorEmail}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryMaterial(null)}>Затвори</Button>
        </DialogActions>
      </Dialog>
    </VendorDashboardLayout>
  );
}
